// Practice Leaderboard — who's rehearsing callbacks in Practice Mode and
// how the AI coach scored them. Rendered on the Manager Ops Dashboard.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rel = (iso) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};

export const PracticeLeaderboardCard = ({ domainId }) => {
  const { token } = useAuth();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!token) return;
    const q = `?days=30${domainId ? `&domain_id=${domainId}` : ''}`;
    axios.get(`${API}/manager/practice-leaderboard${q}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setRows(r.data.leaderboard || []))
      .catch(() => setRows([]));
  }, [token, domainId]);

  return (
    <Card data-testid="practice-leaderboard-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-indigo-500" /> Callback Practice Leaderboard
          <span className="text-[10px] font-normal text-slate-400">last 30 days</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {rows === null ? (
          <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-300" /></div>
        ) : rows.length === 0 ? (
          <p className="text-xs text-slate-400">No practice sessions yet — agents can hit "Practice" on any playbook to rehearse against an AI customer.</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b">
                <th className="py-1.5">Agent</th>
                <th className="py-1.5 text-right">Sessions</th>
                <th className="py-1.5 text-right">Exchanges</th>
                <th className="py-1.5 text-right">Avg coach score</th>
                <th className="py-1.5 text-right">Last practiced</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id} className="border-b last:border-0" data-testid={`practice-row-${r.user_id}`}>
                  <td className="py-1.5 font-medium text-slate-800">{r.name}{r.extension ? <span className="text-slate-400 ml-1 font-mono text-[10px]">{r.extension}</span> : null}</td>
                  <td className="py-1.5 text-right">{r.sessions}</td>
                  <td className="py-1.5 text-right">{r.exchanges}</td>
                  <td className="py-1.5 text-right font-semibold">{r.avg_coach_score != null ? `${r.avg_coach_score}/10` : <span className="text-slate-300 font-normal">—</span>}</td>
                  <td className="py-1.5 text-right text-slate-500">{rel(r.last_practiced)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
};
